import { Button } from '@commenda/kakashi';
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { useEffect, useRef, useState } from 'react';
import { TJsonSchemaFormProps } from './types';
import { FieldErrorTemplate, FieldTemplate } from './widgets';

export const JsonSchemaForm = ({
  schema,
  onSubmit,
  onError,
  onChange,
  onCancel,
  defaultData,
  readonly,
}: TJsonSchemaFormProps) => {
  // RJSF rebuilds its state from the `formData` prop on every parent re-render, so
  // holding the prop at the original data discards whatever the user just entered.
  const [formData, setFormData] = useState(defaultData);
  const seeded = useRef(JSON.stringify(defaultData));
  // Re-entrancy guard for rapid double-clicks (state lags a render tick).
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const incoming = JSON.stringify(defaultData);
    if (incoming === seeded.current) return;
    seeded.current = incoming;
    setFormData(defaultData);
  }, [defaultData]);

  return (
    <Form
      className="flex flex-col gap-xl"
      schema={schema}
      validator={validator}
      formData={formData}
      onChange={(data) => {
        setFormData(data.formData);
        onChange?.(data.formData);
      }}
      onSubmit={async (data) => {
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        setIsSubmitting(true);
        try {
          await onSubmit?.(data.formData);
        } finally {
          isSubmittingRef.current = false;
          setIsSubmitting(false);
        }
      }}
      onError={(errors) => onError?.(errors)}
      showErrorList={false}
      templates={{
        FieldErrorTemplate,
        FieldTemplate,
      }}
      readonly={readonly}
    >
      <div className="flex justify-end gap-md">
        {onCancel && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        )}
        <Button type="submit" size="sm" disabled={readonly || isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </div>
    </Form>
  );
};
